import { useEffect } from 'react';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  canonical?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogUrl?: string;
  schemaData?: object;
}

export const useSEO = ({
  title,
  description,
  keywords,
  canonical,
  ogTitle,
  ogDescription,
  ogImage,
  ogUrl,
  schemaData
}: SEOProps) => {
  useEffect(() => {
    // Title
    if (title) {
      document.title = title;
    }

    // Meta tags
    const metaTags = [
      { name: 'description', content: description },
      { name: 'keywords', content: keywords },
      { property: 'og:title', content: ogTitle || title },
      { property: 'og:description', content: ogDescription || description },
      { property: 'og:image', content: ogImage },
      { property: 'og:url', content: ogUrl },
      { name: 'twitter:title', content: ogTitle || title },
      { name: 'twitter:description', content: ogDescription || description },
      { name: 'twitter:image', content: ogImage }
    ];

    metaTags.forEach(({ name, property, content }) => {
      if (content) {
        const selector = name ? `meta[name="${name}"]` : `meta[property="${property}"]`;
        let element = document.querySelector(selector);
        
        if (!element) {
          element = document.createElement('meta');
          if (name) element.setAttribute('name', name);
          if (property) element.setAttribute('property', property);
          document.head.appendChild(element);
        }
        
        element.setAttribute('content', content);
      }
    });

    // Canonical
    if (canonical) {
      let linkElement = document.querySelector('link[rel="canonical"]');
      if (!linkElement) {
        linkElement = document.createElement('link');
        linkElement.setAttribute('rel', 'canonical');
        document.head.appendChild(linkElement);
      }
      linkElement.setAttribute('href', canonical);
    }

    // Schema.org JSON-LD
    if (schemaData) {
      const scriptId = 'schema-org-jsonld';
      let scriptElement = document.getElementById(scriptId);
      
      if (!scriptElement) {
        scriptElement = document.createElement('script');
        scriptElement.id = scriptId;
        scriptElement.type = 'application/ld+json';
        document.head.appendChild(scriptElement);
      }
      
      scriptElement.textContent = JSON.stringify(schemaData);
    }
  }, [title, description, keywords, canonical, ogTitle, ogDescription, ogImage, ogUrl, schemaData]);
};

export const generateOrganizationSchema = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '株式会社空間Lab',
    url: siteUrl,
    logo: `${siteUrl}/logo.png`,
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '+81-6-6123-1122',
      contactType: 'customer service',
      availableLanguage: 'Japanese',
      areaServed: 'JP'
    },
    address: {
      '@type': 'PostalAddress',
      addressCountry: 'JP',
      addressRegion: '大阪府'
    }
  };
};

export const generateServiceSchema = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  return {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: '大阪府補助金で実現する企業AI研修サービス',
    description: '補助率75%で実質23.75万円から。8時間でChatGPT・Geminiを使った業務効率化とアプリ生成を習得できる企業向けAI研修。',
    provider: {
      '@type': 'Organization',
      name: '株式会社空間Lab',
      telephone: '+81-6-6123-1122'
    },
    areaServed: {
      '@type': 'Place',
      name: '大阪府'
    },
    offers: [
      {
        '@type': 'Offer',
        name: '1-5名プラン',
        price: '237500',
        priceCurrency: 'JPY',
        description: '基礎5時間＋応用3時間、補助金適用後の実質負担額'
      },
      {
        '@type': 'Offer',
        name: '6-10名プラン',
        price: '337500',
        priceCurrency: 'JPY',
        description: '基礎5時間＋応用3時間、補助金適用後の実質負担額'
      }
    ],
    url: siteUrl
  };
};

export const generateFAQSchema = () => {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '補助金が使える条件は？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '大阪府内に事業所を持つ中小企業が対象です。従業員のスキルアップを目的とした研修に対し、補助率75%（上限あり）が適用されます。詳細な条件は無料相談時にご説明いたします。'
        }
      },
      {
        '@type': 'Question',
        name: '社内にIT担当がいなくても大丈夫？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '全く問題ありません。本研修は「ITが苦手な方」でも実務で使えることを前提に設計しています。専門用語を使わず、業務の流れに沿って学べるため、どなたでも安心して受講いただけます。'
        }
      },
      {
        '@type': 'Question',
        name: '研修後に社内展開する方法は？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '研修では「社内で教えられる資料」もお渡しします。また、1ヶ月間のメールサポート期間中に、社内展開の進め方もアドバイスいたします。'
        }
      },
      {
        '@type': 'Question',
        name: '何を事前に準備すればいい？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '特別な準備は不要です。事前ヒアリングで御社の業務内容をお聞きし、最適なカリキュラムを組み立てます。当日はノートPCとインターネット環境があれば受講可能です。'
        }
      },
      {
        '@type': 'Question',
        name: 'オンライン・対面どちらで受講できますか？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '両方に対応しています。オンライン（Zoom等）でも対面でも、内容・サポートは同じです。御社のご都合に合わせてお選びいただけます。'
        }
      }
    ]
  };
};

export const generateWebPageSchema = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  return {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: '大阪府補助金で実現する企業AI研修サービス | 株式会社空間Lab',
    description: '補助率75%で実質23.75万円から。8時間でChatGPT・Geminiを使った業務効率化とアプリ生成を習得できる企業向けAI研修。',
    url: siteUrl,
    inLanguage: 'ja',
    isPartOf: {
      '@type': 'WebSite',
      name: '株式会社空間Lab',
      url: siteUrl
    },
    about: {
      '@type': 'Thing',
      name: 'AI研修',
      description: '企業向けAI研修サービス'
    }
  };
};
